const BAR_BUILDER_TYPES = ['bar', 'groupedBar', 'stackedBar', 'horizontalBar'];
const LINE_BUILDER_TYPES = ['line', 'multiLine', 'multivariate'];
export const isBuilderBarChartType = (chartType) => !!chartType && BAR_BUILDER_TYPES.includes(chartType);
export const isBuilderLineChartType = (chartType) => !!chartType && LINE_BUILDER_TYPES.includes(chartType);
export const getDefaultPaletteForBuilderChartType = (chartType) => {
    if (chartType === 'bar' || chartType === 'horizontalBar') {
        return 'purple';
    }
    if (chartType === 'groupedBar' || chartType === 'stackedBar') {
        return 'categorical';
    }
    if (chartType === 'number') {
        return 'purple';
    }
    if (chartType === 'scatter') {
        return 'purple';
    }
    if (chartType === 'pie') {
        return 'purple';
    }
    if (chartType === 'line') {
        return 'purple';
    }
    if (chartType === 'multiLine' || chartType === 'multivariate') {
        return 'categorical';
    }
    return undefined;
};
export const getPersistedChartType = (state) => {
    if (state.chartType === 'table') {
        return 'table';
    }
    if (state.chartType === 'number') {
        return 'number';
    }
    if (state.chartType === 'line' || state.chartType === 'multiLine' || state.chartType === 'multivariate') {
        return 'line';
    }
    if (state.chartType === 'pie') {
        return 'pie';
    }
    if (state.chartType === 'doughnut') {
        return 'doughnut';
    }
    if (state.chartType === 'bubble') {
        return 'bubble';
    }
    if (state.chartType === 'scatter') {
        return 'scatter';
    }
    if (state.chartType === 'stackedArea') {
        return 'stackedArea';
    }
    if (state.chartType === 'gauge') {
        return 'gauge';
    }
    if (state.orientation === 'horizontal') {
        return 'horizontalBar';
    }
    if (state.chartType === 'stackedBar') {
        return 'stackedBar';
    }
    return 'bar';
};
const getFallbackFieldName = (value) => value.split('.').pop() || value;
const isRawFieldKeyLabel = (label, fieldKey, bareKey) => !label || label === fieldKey || label === bareKey;
/**
 * Resolve a human-readable field label for chart tooltips (SSP-4278).
 *
 * Saved `ColumnConfig.name` is often the bare Cube member key when charts are loaded via
 * `toColumnConfig(stringKey)`. Prefer the live Cube annotation shortTitle/title (dataset display
 * name) in that case, then any caller fallback (e.g. axis title), then the bare key.
 */
export const resolveChartFieldDisplayName = ({ fieldKey, fieldName, annotation, fallback, }) => {
    if (!fieldKey) {
        return fieldName || fallback || '';
    }
    const bareKey = getFallbackFieldName(fieldKey);
    const measure = annotation?.measures?.[fieldKey];
    const dimension = annotation?.dimensions?.[fieldKey];
    const annotationLabel = measure?.shortTitle || measure?.title || dimension?.shortTitle || dimension?.title;
    if (!isRawFieldKeyLabel(fieldName, fieldKey, bareKey)) {
        return fieldName;
    }
    if (!isRawFieldKeyLabel(annotationLabel, fieldKey, bareKey)) {
        return annotationLabel;
    }
    return fallback || annotationLabel || bareKey;
};
export const toColumnConfig = (value, fallback) => {
    if (!value) {
        return undefined;
    }
    if (typeof value !== 'string') {
        return value;
    }
    return {
        key: value,
        name: fallback?.name || getFallbackFieldName(value),
        type: fallback?.type || 'Text',
        role: fallback?.role,
    };
};
export const getBuilderChartTypeFromData = (chartData) => {
    if (chartData?.builderChartType) {
        return chartData.builderChartType;
    }
    if (chartData?.type === 'horizontalBar') {
        return chartData?.splitBy ? 'groupedBar' : 'bar';
    }
    if (chartData?.type === 'bar' && chartData?.splitBy) {
        return 'groupedBar';
    }
    if (chartData?.type === 'line') {
        if (chartData?.additionalMeasures?.length) {
            return 'multivariate';
        }
        if (chartData?.splitBy) {
            return 'multiLine';
        }
        return 'line';
    }
    return chartData?.type || 'table';
};
export const getSelectedFieldsForSettings = (state) => {
    if (state.chartType === 'table') {
        return state.columns || [];
    }
    if (state.chartType === 'number') {
        return (state.numberValues || []).map((item) => item.value);
    }
    if (state.chartType === 'pie') {
        const fields = [];
        if (state.yAxis) {
            fields.push(state.yAxis);
        }
        if (state.xAxis) {
            fields.push(state.xAxis);
        }
        return fields;
    }
    const selectedFields = new Map();
    const addField = (field) => {
        if (field?.key) {
            selectedFields.set(field.key, field);
        }
    };
    addField(state.xAxis);
    addField(state.yAxis);
    addField(state.splitBy);
    for (const measure of state.additionalMeasures || []) {
        addField(measure);
    }
    addField(state.dotSizeField);
    return [...selectedFields.values()];
};
/** Column select options for one label-override row, excluding columns renamed in other rows. */
export const getLabelOverrideColumnOptions = (columns, labelOverrides, rowIndex) => {
    const overrides = labelOverrides || [];
    const currentColumn = overrides[rowIndex]?.column || '';
    const columnsInUseByOtherRows = new Set(overrides.flatMap((override, index) => (index !== rowIndex && override.column ? [override.column] : [])));
    return columns
        .filter((column) => !columnsInUseByOtherRows.has(column.key) || column.key === currentColumn)
        .map((column) => ({ label: column.name, value: column.key }));
};
/** Every configured number value must have a non-empty Label (required in the builder UI). */
export const hasValidNumberChartLabels = (numberValues) => (numberValues ?? []).every((value) => !!value.label?.trim());
/** Whether any table column is still available for a new rename mapping. */
export const hasAvailableLabelOverrideColumns = (columns, labelOverrides) => {
    const renamedKeys = new Set((labelOverrides || []).map((override) => override.column).filter(Boolean));
    return columns.some((column) => !renamedKeys.has(column.key));
};
